import { Component, inject, Input, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { MergeHistoryService } from './merge-history.service';
import { RepoManagementService } from '../repo-management.service';
import { ViewChangesService } from '../view-changes/view-changes.service';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { UtilService } from 'src/app/services/util.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { forkJoin, Subscription } from 'rxjs';
import { GlobalConstants } from 'src/app/base/global.constants';
import { Store } from '@ngrx/store';
import { metaInfo } from 'src/app/STORE/selectors/applifecycle.selector';

@Component({
  selector: 'vb-merge-history',
  templateUrl: './merge-history.component.html',
  styleUrls: ['./merge-history.component.scss'],
  standalone: true,
  imports: [FormsModule, TabViewModule, CommonModule, DialogModule, TableModule, TranslateModule, AvatarModule, ProgressSpinnerModule, TooltipModule, DatePickerModule, FloatLabelModule, ButtonModule, MultiSelectModule, AngularSvgIconModule]
})
export class MergeHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly repoService = inject(RepoManagementService);
  private readonly utilService = inject(UtilService);
  private readonly translate = inject(TranslateService);
  private readonly viewChangesService = inject(ViewChangesService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly store = inject(Store);
  private metaInfoSub: Subscription | null = null;

  @Input() branchId: string;
  @Input() branchName: string;  // Branch name passed directly from parent
  @Input() branchSidToNameMap: Map<string, string> = new Map();
  @Input() applicationType = '';

  isSuiteMode = false;
  
  // Store current branch name for API requests
  private currentBranchName: string = '';
  
  // Local meta info map for component names (populated from API)
  private componentMetaInfo: Record<string, string> = {};
  
  activeTab = 0;
  sortField = 'modifiedDate';
  sortOrder: 'asc' | 'desc' = 'desc';
  mergedComponents: any[] = [];
  mergesFromOtherBranches: any[] = [];
  mergesFromThisBranch: any[] = [];
  visibleMergesFromOther: any[] = [];
  visibleMergesFromThis: any[] = [];
  loading = true;
  dialogLoading = false;
  showComponentsDialog = false;
  mergedComponentsTree: any[] = [];

  // Pagination state per tab
  private readonly pageSize = GlobalConstants.defaultPageSize;
  fromOtherPage = 0;   // current start offset
  fromOtherHasMore = true;
  fromThisPage = 0;
  fromThisHasMore = true;

  // ViewChild refs for scroll containers
  @ViewChild('incomingTableContainer') incomingTableContainer!: ElementRef<HTMLElement>;
  @ViewChild('outgoingTableContainer') outgoingTableContainer!: ElementRef<HTMLElement>;
  
  // Total counts from API for tab badges
  fromOtherBranchesTotal = 0;
  
  // Date format from global constants (includes time)
  readonly dateFormat = GlobalConstants.defaultDateTimeDisplayFormat;
  readonly datePickerFormat = GlobalConstants.dateFormat;
  readonly hourFormat = GlobalConstants.hourFormat;
  
  // Date filters
  startDate: Date | null = null;
  endDate: Date | null = null;
  
  // Branch filters - separate for each tab
  selectedFromBranches: string[] = [];  // For "Merges from other branches" tab
  selectedToBranches: string[] = [];    // For "Merges from this branch" tab
  fromBranchOptions: { label: string; value: string }[] = [];  // Options for From Branch filter
  toBranchOptions: { label: string; value: string }[] = [];    // Options for To Branch filter

  // Merged By filter
  selectedMergedBy: string[] = [];
  mergedByOptions: { label: string; value: string }[] = [];

  // Status filter
  selectedStatuses: string[] = [];
  statusOptions: { label: string; value: string }[] = [];
  
  // Status filter - only show these statuses
  private readonly allowedStatuses = ['MERGE_COMPLETED', 'MERGE_FAILED', 'MERGE_CANCELLED', 'MERGE_COMPLETED_WITH_PUBLISH_ERROR'];
  
  // Status display mapping
  private readonly statusDisplayMap: Record<string, string> = {
    'MERGE_COMPLETED': 'Merge Completed',
    'MERGE_FAILED': 'Merge Failed',
    'MERGE_CANCELLED': 'Merge Skipped',
    'MERGE_COMPLETED_WITH_PUBLISH_ERROR': 'Merge Completed With Publish Error'
  };
  
  /**
   * Get display text for a status
   */
  getStatusDisplay(status: string): string {
    return this.statusDisplayMap[status] || status;
  }
  
  /**
   * Get CSS class for status badge
   */
  getStatusClass(status: string): string {
    switch (status) {
      case 'MERGE_COMPLETED': return 'status-completed';
      case 'MERGE_FAILED': return 'status-failed';
      case 'MERGE_CANCELLED': return 'status-skipped';
      case 'MERGE_COMPLETED_WITH_PUBLISH_ERROR': return 'status-publish-error';
      default: return '';
    }
  }
  
  /**
   * Check if any filter is active
   */
  get hasActiveFilters(): boolean {
    const hasBranchFilter = this.activeTab === 0 
      ? (this.selectedFromBranches && this.selectedFromBranches.length > 0)
      : (this.selectedToBranches && this.selectedToBranches.length > 0);
    return this.startDate !== null || this.endDate !== null || hasBranchFilter
      || (this.selectedMergedBy && this.selectedMergedBy.length > 0)
      || (this.selectedStatuses && this.selectedStatuses.length > 0);
  }
  
  fromThisBranchTotal = 0;

  mergeHistoryService = inject(MergeHistoryService);

  ngOnInit() {
    this.isSuiteMode = this.applicationType === 'SUITE' || this.utilService.isSuiteMode();
    // Subscribe to appmetaInfo store changes so user IDs resolve to names
    this.subscribeToMetaInfoChanges();
    if (this.branchId) {
      // Use branchName from input, fallback to map lookup
      this.currentBranchName = this.branchName || 
        (this.branchSidToNameMap?.get(this.branchId)) || 
        '';
      // Fetch merge history
      this.fetchAllMergeHistory();
    }
  }

  // Track which tabs have scroll listeners attached
  private incomingScrollAttached = false;
  private outgoingScrollAttached = false;

  // Cleanup references for scroll listeners and timeouts (prevent memory leaks)
  private scrollListenerCleanups: (() => void)[] = [];
  private pendingTimeouts: any[] = [];

  ngAfterViewInit(): void {
    // Attach scroll for the initially visible tab (incoming)
    const id = setTimeout(() => this.tryAttachScroll('fromOtherBranches'), 300);
    this.pendingTimeouts.push(id);
  }

  private tryAttachScroll(tab: 'fromOtherBranches' | 'fromThisBranch'): void {
    if (tab === 'fromOtherBranches' && !this.incomingScrollAttached) {
      if (this.attachScrollToTable(this.incomingTableContainer, 'fromOtherBranches')) {
        this.incomingScrollAttached = true;
      }
    }
    if (tab === 'fromThisBranch' && !this.outgoingScrollAttached) {
      if (this.attachScrollToTable(this.outgoingTableContainer, 'fromThisBranch')) {
        this.outgoingScrollAttached = true;
      }
    }
  }

  private attachScrollToTable(container: ElementRef<HTMLElement> | undefined, tab: 'fromOtherBranches' | 'fromThisBranch'): boolean {
    if (!container?.nativeElement) {
      return false;
    }
    const scrollWrapper = container.nativeElement.querySelector(
      '.p-datatable-wrapper, .p-datatable-table-container'
    );
    if (scrollWrapper) {
      const handler = () => {
        const el = scrollWrapper as HTMLElement;
        if (el.offsetHeight + el.scrollTop + 1 >= el.scrollHeight) {
          this.loadMore(tab);
        }
      };
      scrollWrapper.addEventListener('scroll', handler);
      this.scrollListenerCleanups.push(() => scrollWrapper.removeEventListener('scroll', handler));
      return true;
    }
    return false;
  }

  loadMore(tab: 'fromOtherBranches' | 'fromThisBranch'): void {
    if (this.loading) return;
    const hasMore = tab === 'fromOtherBranches' ? this.fromOtherHasMore : this.fromThisHasMore;
    if (!hasMore) return;
    this.fetchMergeHistory(tab, true);
  }

  /**
   * Fetch component meta info using getBranchMetaInfo API
   */
  private fetchComponentMetaInfo(): void {
    this.loading = true;
    this.viewChangesService.getBranchMetaInfo(this.branchId).subscribe({
      next: (response: any) => {
        this.populateComponentMetaInfo(response);
        // Fetch both tabs' merge history in parallel for faster loading
        this.fetchAllMergeHistory();
      },
      error: () => {
        // Still load merge history even if meta info fails
        this.fetchAllMergeHistory();
      }
    });
  }

  /**
   * Fetch both tabs' merge history in parallel using forkJoin
   */
  private fetchAllMergeHistory(): void {
    this.loading = true;
    // Reset pagination
    this.fromOtherPage = 0;
    this.fromOtherHasMore = true;
    this.fromThisPage = 0;
    this.fromThisHasMore = true;
    this.mergesFromOtherBranches = [];
    this.mergesFromThisBranch = [];

    const fromOtherRequest = this.buildMergeHistoryRequest('fromOtherBranches', 0);
    const fromThisRequest = this.buildMergeHistoryRequest('fromThisBranch', 0);

    forkJoin({
      fromOther: this.mergeHistoryService.getMergeHistory(fromOtherRequest),
      fromThis: this.mergeHistoryService.getMergeHistory(fromThisRequest)
    }).subscribe({
      next: ({ fromOther, fromThis }) => {
        // Process fromOtherBranches (status filtering done server-side via API payload)
        const fromOtherResults = fromOther.results || [];
        this.fromOtherBranchesTotal = fromOther.total || fromOtherResults.length;
        const metaInfo = this.appGlobalService.get('appmetaInfo') || {};
        const mappedOther = fromOtherResults.map((item: any) => ({
          ...item,
          mergedDate: item.modifiedDate,
          modifiedById: item.modifiedBy,
          modifiedBy: this.utilService.normalizeDisplayName(metaInfo[item.modifiedBy] || item.modifiedBy),
          fromBranch: item.sourceBranchName || item.sourceBranchId,
          toBranch: item.targetBranchName || item.targetBranchId
        }));
        this.mergesFromOtherBranches = this.buildFlatRowsWithMembers(mappedOther, 'fromOtherBranches');
        this.fromOtherPage = this.pageSize;
        this.fromOtherHasMore = fromOtherResults.length >= this.pageSize;
        this.rebuildFilterOptions();

        // Process fromThisBranch (status filtering done server-side via API payload)
        const fromThisResults = fromThis.results || [];
        this.fromThisBranchTotal = fromThis.total || fromThisResults.length;
        const mappedThis = fromThisResults.map((item: any) => ({
          ...item,
          mergedDate: item.modifiedDate,
          modifiedById: item.modifiedBy,
          modifiedBy: this.utilService.normalizeDisplayName(metaInfo[item.modifiedBy] || item.modifiedBy),
          fromBranch: item.sourceBranchName || item.sourceBranchId,
          toBranch: item.targetBranchName || item.targetBranchId
        }));
        this.mergesFromThisBranch = this.buildFlatRowsWithMembers(mappedThis, 'fromThisBranch');
        this.fromThisPage = this.pageSize;
        this.fromThisHasMore = fromThisResults.length >= this.pageSize;
        this.rebuildFilterOptions();
        this.updateVisibleRows();

        this.loading = false;
      },
      error: () => {
        this.mergesFromOtherBranches = [];
        this.mergesFromThisBranch = [];
        this.visibleMergesFromOther = [];
        this.visibleMergesFromThis = [];
        this.loading = false;
      }
    });
  }

  /**
   * Populate componentMetaInfo from API response
   */
  private populateComponentMetaInfo(response: any): void {
    const dataArray = Array.isArray(response) ? response : (response?.results || response?.data || []);
    if (dataArray && Array.isArray(dataArray)) {
      for (const item of dataArray) {
        if (item.modelId && item.name) {
          this.componentMetaInfo[item.modelId] = item.name;
        }
        if (item.sid && item.name) {
          this.componentMetaInfo[item.sid] = item.name;
        }
      }
    }
  }

  fetchMergeHistory(tab: 'fromOtherBranches' | 'fromThisBranch', append = false) {
    this.loading = true;

    let startOffset: number;
    if (append) {
      startOffset = tab === 'fromOtherBranches' ? this.fromOtherPage : this.fromThisPage;
    } else {
      startOffset = 0;
      if (tab === 'fromOtherBranches') {
        this.fromOtherPage = 0;
        this.fromOtherHasMore = true;
        this.mergesFromOtherBranches = [];
      } else {
        this.fromThisPage = 0;
        this.fromThisHasMore = true;
        this.mergesFromThisBranch = [];
      }
    }

    const requestPayload = this.buildMergeHistoryRequest(tab, startOffset);

    this.mergeHistoryService.getMergeHistory(requestPayload).subscribe({
      next: (data) => {
        // API returns { results: [...], page, pageSize, total, filtered }
        // Status filtering done server-side via API payload
        const apiResults = data.results || [];
        const metaInfo = this.appGlobalService.get('appmetaInfo') || {};
        const mappedResults = apiResults.map((item: any) => ({
          ...item,
          mergedDate: item.modifiedDate,
          modifiedById: item.modifiedBy,
          modifiedBy: this.utilService.normalizeDisplayName(metaInfo[item.modifiedBy] || item.modifiedBy),
          fromBranch: item.sourceBranchName || item.sourceBranchId,
          toBranch: item.targetBranchName || item.targetBranchId
        }));
        const results = this.buildFlatRowsWithMembers(mappedResults, tab);
        
        if (tab === 'fromOtherBranches') {
          this.mergesFromOtherBranches = append ? [...this.mergesFromOtherBranches, ...results] : results;
          this.fromOtherPage = (append ? this.fromOtherPage : 0) + this.pageSize;
          this.fromOtherHasMore = apiResults.length >= this.pageSize;
          if (!append) this.fromOtherBranchesTotal = data.total || apiResults.length;
        } else {
          this.mergesFromThisBranch = append ? [...this.mergesFromThisBranch, ...results] : results;
          this.fromThisPage = (append ? this.fromThisPage : 0) + this.pageSize;
          this.fromThisHasMore = apiResults.length >= this.pageSize;
          if (!append) this.fromThisBranchTotal = data.total || apiResults.length;
        }
        this.rebuildFilterOptions();
        this.updateVisibleRows();
        this.loading = false;
      },
      error: () => {
        if (tab === 'fromOtherBranches') {
          this.mergesFromOtherBranches = [];
          this.visibleMergesFromOther = [];
        } else {
          this.mergesFromThisBranch = [];
          this.visibleMergesFromThis = [];
        }
        this.loading = false;
      }
    });
  }

  get filteredMergesFromOtherBranches() {
    return this.filterAndSort(this.mergesFromOtherBranches, ['sourceBranchName', 'mergedDate', 'modifiedBy']);
  }

  get filteredMergesFromThisBranch() {
    return this.filterAndSort(this.mergesFromThisBranch, ['targetBranchName', 'mergedDate', 'modifiedBy']);
  }

  /**
   * Rebuild filter dropdown options from the currently loaded table data.
   * Accumulates options across pages without losing previously seen values.
   */
  private rebuildFilterOptions(): void {
    const mergeOpts = (
      existing: { label: string; value: string }[],
      newValues: string[],
      labelFn?: (v: string) => string
    ): { label: string; value: string }[] => {
      const map = new Map<string, string>();
      for (const opt of existing) {
        map.set(opt.value, opt.label);
      }
      for (const v of newValues) {
        if (v && !map.has(v)) {
          map.set(v, labelFn ? labelFn(v) : v);
        }
      }
      return Array.from(map.entries()).map(([value, label]) => ({ label, value }));
    };

    const allData = [...this.mergesFromOtherBranches, ...this.mergesFromThisBranch];

    // From Branch options (source branch names from incoming merges)
    const fromBranches = this.mergesFromOtherBranches.map((d) => d.sourceBranchName).filter((v): v is string => !!v);
    this.fromBranchOptions = mergeOpts(this.fromBranchOptions, fromBranches);

    // To Branch options (target branch names from outgoing merges)
    const toBranches = this.mergesFromThisBranch.map((d) => d.targetBranchName).filter((v): v is string => !!v);
    this.toBranchOptions = mergeOpts(this.toBranchOptions, toBranches);

    // Merged By options - resolve IDs to names
    const metaInfo = this.appGlobalService.get('appmetaInfo') || {};
    const mergedByIds = allData.map((d) => d.modifiedById).filter((v): v is string => !!v);
    this.mergedByOptions = mergeOpts(
      this.mergedByOptions,
      mergedByIds,
      (id) => this.utilService.normalizeDisplayName(metaInfo[id] || id)
    );

    // Status options
    const statuses = allData.map((d) => d.status).filter((v): v is string => !!v);
    this.statusOptions = mergeOpts(
      this.statusOptions,
      statuses,
      (v) => this.getStatusDisplay(v)
    );
  }

  filterAndSort(data: any[], columns: string[]) {
    return data.sort((a, b) => {
      const aVal = a[this.sortField];
      const bVal = b[this.sortField];
      if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
      if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
      return 0;
    });
  }

  setSort(field: string) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'desc';
      }
      const tab = this.activeTab === 0 ? 'fromOtherBranches' : 'fromThisBranch';
      this.fetchMergeHistory(tab);
    }

  viewComponents(merge: any) {
    this.mergedComponents = [];
    this.mergedComponentsTree = [];
    this.showComponentsDialog = true;
    this.dialogLoading = true;

    // Fetch the full merge record by sid to get merged components
    const mergeId = merge.sid;
    if (mergeId) {
      this.mergeHistoryService.getMergeHistoryById(mergeId).subscribe({
        next: (fullMerge: any) => {
          this.fetchMetaAndShowComponents(fullMerge);
        },
        error: () => {
          this.fetchMetaAndShowComponents(merge);
        }
      });
    } else {
      this.fetchMetaAndShowComponents(merge);
    }
  }

  /**
   * Fetch branch metadata then display merged components
   */
  private fetchMetaAndShowComponents(merge: any): void {
    const sourceBranchId = merge.sourceBranchId;
    if (sourceBranchId) {
      this.viewChangesService.getBranchMetaInfo(sourceBranchId).subscribe({
        next: (response: any) => {
          this.populateComponentMetaInfo(response);
          this.processAndDisplayComponents(merge);
          this.dialogLoading = false;
        },
        error: () => {
          this.processAndDisplayComponents(merge);
          this.dialogLoading = false;
        }
      });
    } else {
      this.processAndDisplayComponents(merge);
      this.dialogLoading = false;
    }
  }

  // Component types without AppMetaInfo - use translated type as name
  private readonly componentsWithoutAppMetaInfo = new Set([
    'TECHNOLOGY_CHOICES',
    'APP_LANGUAGES',
    'QUERYVIEW_CUD_CONFIGURATION',
    'APP_SETTINGS',
    'APPLICATION_CONFIGURATION',
    'SEARCHABLE_INDEX_FIELD',
    'RUNTIME_CONFIGURATION',
    'MAIL_INFO',
    'ATTACHMENT',
    'WORKFLOW_ACTION',
    'WORKFLOW_ACTOR',
    'WORKFLOW_MAIL_INFO',
    'TEMPLATE'
  ]);

  /**
   * Process merged components and display in dialog.
   * mergedComponents format: { TYPE: [componentId1, componentId2, ...] }
   * componentActionsMap format: { componentId: actionString }
   * Component names are resolved via getComponentName() (uses componentMetaInfo populated from branch meta API).
   * If no name is found, the raw component ID is shown as fallback.
   */
  private processAndDisplayComponents(merge: any): void {
    const componentsMap = merge.mergedComponents || {};
    const componentActionsMap = merge.componentActionsMap || {};
    const componentAppNameMap = merge.componentAppNameMap || {};
    const items: any[] = [];

    for (const componentType of Object.keys(componentsMap)) {
      const componentIds = componentsMap[componentType] || [];
      for (const componentId of componentIds as string[]) {
        // For components without AppMetaInfo, use translated component type as name
        let componentName: string;
        if (this.componentsWithoutAppMetaInfo.has(componentType)) {
          componentName = this.translate.instant(componentType);
        } else {
          componentName = this.getComponentName(componentId) || componentId;
        }      
        const action = componentActionsMap[componentId] || '';
        const item: any = {
          type: this.formatComponentType(componentType),
          name: componentName,
          action: this.utilService.formatAction(action)
        };
        if (this.isSuiteMode) {
          item.appName = componentAppNameMap[componentId] || '';
        }
        items.push(item);
      }
    }

    // Assign new array reference so PrimeNG table detects the change
    this.mergedComponents = items;
    this.mergedComponentsTree = items.map(item => ({ data: item }));
  }

  /**
   * Get component name by ID from componentMetaInfo, branchSidToNameMap, or appMetaInfo
   */
  private getComponentName(id: string): string | undefined {
    if (!id) {
      return undefined;
    }
    if (this.componentMetaInfo[id]) {
      return this.componentMetaInfo[id];
    }
    if (this.branchSidToNameMap?.has(id)) {
      return this.branchSidToNameMap.get(id);
    }
    
    // Fallback to global appmetaInfo
    const globalName = this.utilService.getNameFromAppMetaInfo(id);
    if (globalName) {
      return globalName;
    }
    
    return undefined;
  }

  /**
   * Get translated component type label from en.json
   * Falls back to formatted type if translation key not found
   */
  private formatComponentType(type: string): string {
    // Try to get translation using the type as key (e.g., PAGE, PROVIDER, TECHNOLOGY_CHOICES)
    const translatedValue = this.translate.instant(type);
    // If translation returns the same key, it means no translation found - return formatted fallback
    if (translatedValue === type) {
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    return translatedValue;
  }

  buildMergeHistoryRequest(tab: 'fromOtherBranches' | 'fromThisBranch', start = 0): any {
    const payload: any = {
      start,
      length: this.pageSize
    };

    if (this.sortField) {
      payload.order = [{column: this.sortField === 'mergedDate' ? 'modifiedDate' : this.sortField,
        dir: this.sortOrder
      }];
    }

    const search = this.buildSearchFilters(tab);

    if (Object.keys(search).length > 0) {
      payload.search = search;
    }

    return payload;
  }

  private buildSearchFilters(tab: 'fromOtherBranches' | 'fromThisBranch'): any {
    const search: any = {};

    this.applyBranchIdFilter(search, tab);
    this.applyDateFilters(search);
    this.applyBranchNameFilter(search, tab);

    if (this.selectedMergedBy?.length > 0) {
      search.modifiedBy = this.selectedMergedBy;
    }

    search.status = this.selectedStatuses?.length > 0 ? this.selectedStatuses : this.allowedStatuses;

    return search;
  }

  private applyBranchIdFilter(search: any, tab: 'fromOtherBranches' | 'fromThisBranch'): void {
    if (!this.branchId) return;
    const key = tab === 'fromOtherBranches' ? 'targetBranchId' : 'sourceBranchId';
    search[key] = this.branchId;
  }

  private applyDateFilters(search: any): void {
    if (this.startDate) {
      search.modifiedDateFrom = this.startDate.getTime();
    }
    if (this.endDate) {
      search.modifiedDateTo = this.endDate.getTime();
    }
  }

  private applyBranchNameFilter(search: any, tab: 'fromOtherBranches' | 'fromThisBranch'): void {
    if (tab === 'fromOtherBranches' && this.selectedFromBranches?.length > 0) {
      search.sourceBranchName = this.selectedFromBranches;
    }
    if (tab === 'fromThisBranch' && this.selectedToBranches?.length > 0) {
      search.targetBranchName = this.selectedToBranches;
    }
  }

  onFilterChange() {
    const tab = this.activeTab === 0 ? 'fromOtherBranches' : 'fromThisBranch';
    this.fetchMergeHistory(tab);
  }

  clearFilters() {
    this.startDate = null;
    this.endDate = null;
    this.selectedFromBranches = [];
    this.selectedToBranches = [];
    this.selectedMergedBy = [];
    this.selectedStatuses = [];
    this.onFilterChange();
  }

  onDateFilterChange() {
    this.onFilterChange();
  }

onTabChange(index: number) {
  this.activeTab = index;
  const tab = index === 0 ? 'fromOtherBranches' : 'fromThisBranch';
  this.fetchMergeHistory(tab);
  // Attach scroll listener for newly rendered tab content (lazy rendering)
  const id = setTimeout(() => this.tryAttachScroll(tab), 300);
  this.pendingTimeouts.push(id);
}

  /**
   * Flatten suite merges into normal rows and attach applicationName.
   */
  private buildFlatRowsWithMembers(items: any[], tab: 'fromOtherBranches' | 'fromThisBranch'): any[] {
    const rows: any[] = [];
    for (const item of items) {
        rows.push({
          ...item
        });
      }
    return rows;
  }

  /** Builds one row per suite member, or a single row for non-suite / memberless items. */
  private buildRowsForItem(item: any): any[] {
    const memberEntries = this.getSuiteMemberEntries(item);
    if (memberEntries.length === 0) {
      return [{ ...item, applicationName: this.getApplicationName(item) }];
    }
    return memberEntries.map(([appId, member]) => ({
      ...item,
      applicationName: this.getApplicationName(item, appId, member?.name),
      sourceBranchName: item.sourceBranchName,
      targetBranchName: item.targetBranchName
    }));
  }

  /** Returns suite member branch-pair entries, or an empty array for non-suite merges. */
  private getSuiteMemberEntries(item: any): [string, any][] {
    if (!item.suiteMerge) {
      return [];
    }
    const members =
      item.memberBranchPairs && Object.keys(item.memberBranchPairs).length > 0 ? item.memberBranchPairs : {};
    return Object.entries(members);
  }

  /**
   * Keep visible rows aligned with the currently loaded flat data.
   */
  private updateVisibleRows(): void {
    this.visibleMergesFromOther = [...this.mergesFromOtherBranches];
    this.visibleMergesFromThis = [...this.mergesFromThisBranch];
  }

  private getApplicationName(item: any, appId?: string, memberName?: string): string {
    if (memberName) {
      return memberName;
    }

    return item.applicationName
      || item.appName
      || item.name
      || this.utilService.getNameFromAppMetaInfo(appId || item.sourceAppId || item.targetAppId)
      || '';
  }

  getBgColor(str: string, saturation = 50, lightness = 50): string {
    return this.repoService.getBgColor(str, saturation, lightness);
  }

  getOptionLabel(options: { label: string; value: string }[], value: string): string {
    const opt = options.find(o => o.value === value);
    return opt ? opt.label : value;
  }

  /**
   * Subscribe to ngrx metaInfo store changes. When the Firestore listener in header
   * updates appmetaInfo, re-enrich displayed data so user IDs resolve to names.
   */

  private subscribeToMetaInfoChanges(): void {
    this.metaInfoSub = this.store.select(metaInfo).subscribe(() => {
      this.enrichMergeDisplayNames();
    });
  }

  /**
   * Re-enrich modifiedBy display names from the latest appmetaInfo for both tabs.
   * Also rebuilds mergedBy filter options so labels update.
   */
  private enrichMergeDisplayNames(): void {
    const meta = this.appGlobalService.get('appmetaInfo') || {};

    for (const item of this.mergesFromOtherBranches) {
      if (item.modifiedById) {
        item.modifiedBy = this.utilService.normalizeDisplayName(meta[item.modifiedById] || item.modifiedById);
      }
    }
    for (const item of this.mergesFromThisBranch) {
      if (item.modifiedById) {
        item.modifiedBy = this.utilService.normalizeDisplayName(meta[item.modifiedById] || item.modifiedById);
      }
    }

    // Rebuild tree references so PrimeNG detects the change
    this.updateVisibleRows();

    // Rebuild mergedBy filter options with updated names
    this.rebuildFilterOptions();
  }

  ngOnDestroy(): void {
    if (this.metaInfoSub) {
      this.metaInfoSub.unsubscribe();
    }
    // Remove scroll event listeners to prevent memory leaks
    for (const cleanup of this.scrollListenerCleanups) {
      cleanup();
    }
    this.scrollListenerCleanups = [];
    // Clear pending timeouts
    for (const id of this.pendingTimeouts) {
      clearTimeout(id);
    }
    this.pendingTimeouts = [];
  }
}
